import React, { useState, useEffect } from 'react';
import { toStringOption, QuestionOption, StringOption } from '../../data/types';
import { useParams, useNavigate } from 'react-router-dom';
import { Question, Standard, UserRole } from '../../data/types';
import { dataService } from '../../services/dataService';
import SimpleRichTextEditor from '../SimpleRichTextEditor';
import QuestionOptionInput, { OptionItem } from '../QuestionOptionInput';
import { mockQuestions } from '../../data/mockQuestions';
import { Card } from '../Card';

// Helper function to safely get text from a QuestionOption (string or StringOption)
const getOptionText = (option: QuestionOption): string => {
  if (typeof option === 'string') {
    return option;
  }
  return option.text;
};

interface QuestionDetailScreenProps {
  userRole: UserRole;
  mode?: 'view' | 'edit';
}

const QuestionDetailScreen: React.FC<QuestionDetailScreenProps> = ({ userRole, mode = 'view' }) => {
  const { questionId } = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const [question, setQuestion] = useState<Question | null>(null);
  const [standard, setStandard] = useState<Standard | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Always use OptionItem[] for options in formData
  const [formData, setFormData] = useState<Partial<Question> & { options?: OptionItem[], useHtmlFormat?: boolean }>({});
  const [activeTab, setActiveTab] = useState<'general' | 'options' | 'hints'>('general');
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Determine if the user can edit based on role and mode
  const canEdit = userRole === 'SuperAdmin' && mode === 'edit';

  useEffect(() => {
    const fetchQuestionData = async () => {
      if (!questionId) return;
      
      try {
        setLoading(true);
        // Get question data
        const questions = await dataService.getQuestions();
        const questionData = questions.find(q => q.id === questionId);
        if (!questionData) {
          console.error('Question not found');
          return;
        }
        // Convert options to OptionItem[] if necessary
        let options: OptionItem[] = [];
        if (Array.isArray(questionData.options)) {
          if (questionData.options.length > 0 && typeof questionData.options[0] === 'object' && 'text' in questionData.options[0]) {
            // Already OptionItem[]
            options = (questionData.options as unknown) as OptionItem[];
          } else {
            // Assume string[] and convert
            options = ((questionData.options as unknown) as string[]).map(text => ({ text }));
          }
        }
        setQuestion(questionData);
        setFormData({ ...questionData, options });
        
        // Fetch the standard information
        const standards = await dataService.getStandards();
        const standardData = standards.find(s => s.id === questionData.standardId) || null;
        setStandard(standardData);
      } catch (error) {
        console.error('Error fetching question data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuestionData();
  }, [questionId]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleOptionsChange = (index: number, value: string) => {
    if (!formData.options) return;
    const newOptions = [...formData.options];
    
    // Handle both string and object types
    if (typeof newOptions[index] === 'string') {
      newOptions[index] = toStringOption(value);
    } else {
      // It's already an object, so we can safely spread it
      newOptions[index] = { ...(newOptions[index] as StringOption), text: value };
    }
    
    setFormData({
      ...formData,
      options: newOptions
    });
  };
  
  const addOption = () => {
    setFormData({
      ...formData,
      options: [...(formData.options || []), { text: '', image: undefined }]
    });
  };
  
  const removeOption = (index: number) => {
    if (!formData.options) return;
    
    const newOptions = [...formData.options];
    newOptions.splice(index, 1);
    
    setFormData({
      ...formData,
      options: newOptions
    });
  };
  
  const handleHintsChange = (index: number, value: string) => {
    if (!formData.hints) return;
    
    const newHints = [...formData.hints];
    newHints[index] = value;
    
    setFormData({
      ...formData,
      hints: newHints
    });
  };
  
  const addHint = () => {
    setFormData({
      ...formData,
      hints: [...(formData.hints || []), '']
    });
  };
  
  const removeHint = (index: number) => {
    if (!formData.hints) return;
    
    const newHints = [...formData.hints];
    newHints.splice(index, 1);
    
    setFormData({
      ...formData,
      hints: newHints
    });
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.text || formData.text.trim() === '') {
      newErrors.text = 'Question text is required';
    }
    
    if (!formData.standardId || formData.standardId.trim() === '') {
      newErrors.standardId = 'Standard is required';
    }
    
    if (!formData.type || formData.type.trim() === '') {
      newErrors.type = 'Question type is required';
    }
    
    if (!formData.difficulty || formData.difficulty.trim() === '') {
      newErrors.difficulty = 'Difficulty level is required';
    }
    
    // Validation for correct answer based on type
    if (formData.type === 'MultipleSelection') {
      if (!Array.isArray(formData.correctAnswer) || formData.correctAnswer.length === 0) {
        newErrors.correctAnswer = 'Select at least one correct answer.';
      }
    } else if (formData.type === 'MultipleChoice' || formData.type === 'YesNo') {
      if (!formData.correctAnswer || typeof formData.correctAnswer !== 'string' || formData.correctAnswer.trim() === '') {
        newErrors.correctAnswer = 'Select the correct answer.';
      }
    } else if (formData.type === 'FillBlank' || formData.type === 'TypeIn' || formData.type === 'DragAndDrop') {
      if (
        !formData.correctAnswer ||
        typeof formData.correctAnswer !== 'string' ||
        formData.correctAnswer.trim() === ''
      ) {
        newErrors.correctAnswer = 'Correct answer is required.';
      }
    }
    
    if (formData.type === 'MultipleChoice' && (!formData.options || formData.options.length < 2)) {
      newErrors.options = 'Multiple choice questions require at least 2 options';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      // Get current questions
      const questions = await dataService.getQuestions();
      const index = questions.findIndex(q => q.id === questionId);
      
      if (index !== -1) {
        // Update the question with new data
        mockQuestions[index] = {
          ...questions[index],
          ...formData,
          id: questionId! // Ensure ID doesn't change
        } as Question;
      }
      
      // Navigate back to the question management screen
      navigate('/admin/questions');
    } catch (error) {
      console.error('Error saving question:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const renderGeneralTab = () => {
    return (
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="text" className="block text-sm font-medium text-gray-700">
              Question Text / Description
            </label>
            {canEdit && (
              <div className="flex items-center">
                <label className="inline-flex items-center mr-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    checked={formData.useHtmlFormat || false}
                    onChange={(e) => setFormData({ ...formData, useHtmlFormat: e.target.checked })}
                  />
                  <span className="ml-2 text-sm text-gray-600">Enable formatting</span>
                </label>
              </div>
            )}
          </div>
          {formData.useHtmlFormat ? (
            <SimpleRichTextEditor
              value={formData.text || ''}
              onChange={(value: string) => setFormData({ ...formData, text: value })}
              placeholder="Enter the question text or description"
              disabled={!canEdit}
              error={!!errors.text}
              className="w-full"
            />
          ) : (
            <textarea
              id="text"
              name="text"
              rows={4}
              className={`w-full px-3 py-2 border ${errors.text ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              placeholder="Enter the question text or description"
              value={formData.text || ''}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          )}
          {errors.text && <p className="mt-1 text-sm text-red-500">{errors.text}</p>}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="standardId" className="block text-sm font-medium text-gray-700 mb-1">
              Standard
            </label>
            <select
              id="standardId"
              name="standardId"
              className={`w-full px-3 py-2 border ${errors.standardId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              value={formData.standardId || ''}
              onChange={handleInputChange}
              disabled={!canEdit}
            >
              <option value="">Select a standard</option>
              {standard && (
                <option value={standard.id}>{standard.code} - {standard.description}</option>
              )}
            </select>
            {errors.standardId && <p className="mt-1 text-sm text-red-500">{errors.standardId}</p>}
          </div>
          
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Question Type
            </label>
            <select
              id="type"
              name="type"
              className={`w-full px-3 py-2 border ${errors.type ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              value={formData.type || ''}
              onChange={handleInputChange}
              disabled={!canEdit}
            >
              <option value="">Select a type</option>
              <option value="MultipleChoice">Multiple Choice</option>
              <option value="TypeIn">Type In</option>
              <option value="DragAndDrop">Drag and Drop</option>
              <option value="Matching">Matching</option>
              <option value="MultipleSelection">Multiple Selection</option>
              <option value="YesNo">Yes/No</option>
              <option value="FillBlank">Fill in the Blank</option>
            </select>
            {errors.type && <p className="mt-1 text-sm text-red-500">{errors.type}</p>}
            
            {formData.type === 'TypeIn' && (
              <p className="mt-1 text-xs text-gray-500">TypeIn: Students enter a complete answer in a text field</p>
            )}
            {formData.type === 'FillBlank' && (
              <p className="mt-1 text-xs text-gray-500">FillBlank: Use [blank] in your question text to indicate where students should fill in missing words</p>
            )}
          </div>
        </div>

        {/* Answer Options - now part of General tab */}
        {(formData.type === 'MultipleChoice' || formData.type === 'MultipleSelection' || formData.type === 'Matching' || formData.type === 'DragAndDrop' || formData.type === 'FillBlank') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Answer Options {formData.type === 'MultipleChoice' || formData.type === 'MultipleSelection' ? '(Select correct answer' + (formData.type === 'MultipleSelection' ? 's' : '') + ' below)' : ''}
            </label>
            {formData.type === 'MultipleChoice' && (
              <div className="space-y-2 mb-3">
                {(formData.options || []).map((option, idx) => (
                  <div key={idx} className="flex items-center">
                    <input
                      type="radio"
                      name="correctOptionMC"
                      checked={formData.correctAnswer === getOptionText(option)}
                      onChange={() => {
                        if (canEdit) {
                          setFormData({ ...formData, correctAnswer: getOptionText(option) });
                        }
                      }}
                      disabled={!canEdit}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 mr-2"
                    />
                    <input
                      type="text"
                      value={getOptionText(option)}
                      onChange={(e) => {
                        if (canEdit) {
                          const newOptions = [...(formData.options || [])];
                          newOptions[idx] = typeof option === 'string' ? { text: e.target.value } : { ...(option as StringOption), text: e.target.value };
                          // Update correctAnswer if this option was selected
                          if (formData.correctAnswer === getOptionText(option)) {
                            setFormData({ ...formData, options: newOptions, correctAnswer: e.target.value });
                          } else {
                            setFormData({ ...formData, options: newOptions });
                          }
                        }
                      }}
                      disabled={!canEdit}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    />
                    {canEdit && (
                      <button 
                        type="button" 
                        className="ml-2 text-red-500"
                        onClick={() => removeOption(idx)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button 
                    type="button" 
                    className="mt-2 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark"
                    onClick={addOption}
                  >
                    Add Option
                  </button>
                )}
              </div>
            )}
            {formData.type === 'MultipleSelection' && (
              <div className="space-y-2 mb-3">
                {(formData.options || []).map((option, idx) => (
                  <div key={idx} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={
                        Array.isArray(formData.correctAnswer) 
                          ? formData.correctAnswer.includes(getOptionText(option))
                          : typeof formData.correctAnswer === 'string'
                            ? formData.correctAnswer.split(',').includes(getOptionText(option))
                            : false
                      }
                      onChange={(e) => {
                        if (!canEdit) return;
                        let newCorrect = Array.isArray(formData.correctAnswer) 
                          ? [...formData.correctAnswer] 
                          : typeof formData.correctAnswer === 'string'
                            ? formData.correctAnswer.split(',')
                            : [];
                        
                        if (e.target.checked) {
                          newCorrect.push(getOptionText(option));
                        } else {
                          newCorrect = newCorrect.filter(ans => ans !== getOptionText(option));
                        }
                        setFormData({ ...formData, correctAnswer: newCorrect });
                      }}
                      disabled={!canEdit}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mr-2"
                    />
                    <input
                      type="text"
                      value={getOptionText(option)}
                      onChange={(e) => {
                        if (canEdit) {
                          const newOptions = [...(formData.options || [])];
                          newOptions[idx] = typeof option === 'string' ? { text: e.target.value } : { ...(option as StringOption), text: e.target.value };
                          // Update correctAnswer if this option was selected
                          if (Array.isArray(formData.correctAnswer) && formData.correctAnswer.includes(getOptionText(option))) {
                            const newCorrect = formData.correctAnswer.map(ans => 
                              ans === getOptionText(option) ? e.target.value : ans
                            );
                            setFormData({ ...formData, options: newOptions, correctAnswer: newCorrect });
                          } else {
                            setFormData({ ...formData, options: newOptions });
                          }
                        }
                      }}
                      disabled={!canEdit}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    />
                    {canEdit && (
                      <button 
                        type="button" 
                        className="ml-2 text-red-500"
                        onClick={() => removeOption(idx)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button 
                    type="button" 
                    className="mt-2 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark"
                    onClick={addOption}
                  >
                    Add Option
                  </button>
                )}
              </div>
            )}
            {formData.type !== 'MultipleChoice' && formData.type !== 'MultipleSelection' && (
              <QuestionOptionInput
                options={formData.options ?? []}
                type={formData.type ?? ''}
                canEdit={canEdit}
                onChange={opts => setFormData({ ...formData, options: opts })}
              />
            )}
          </div>
        )}
        
        {/* Yes/No question type - simplified UI with just radio buttons */}
        {formData.type === 'YesNo' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correct Answer
            </label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="yes-option"
                  name="yesno-correct"
                  value="Yes"
                  checked={formData.correctAnswer === 'Yes'}
                  onChange={() => canEdit && setFormData({ ...formData, correctAnswer: 'Yes' })}
                  disabled={!canEdit}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                />
                <label htmlFor="yes-option" className="text-gray-700">Yes</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="no-option"
                  name="yesno-correct"
                  value="No"
                  checked={formData.correctAnswer === 'No'}
                  onChange={() => canEdit && setFormData({ ...formData, correctAnswer: 'No' })}
                  disabled={!canEdit}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                />
                <label htmlFor="no-option" className="text-gray-700">No</label>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
              Difficulty
            </label>
            <select
              id="difficulty"
              name="difficulty"
              className={`w-full px-3 py-2 border ${errors.difficulty ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              value={formData.difficulty || ''}
              onChange={handleInputChange}
              disabled={!canEdit}
            >
              <option value="">Select difficulty</option>
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
            {errors.difficulty && <p className="mt-1 text-sm text-red-500">{errors.difficulty}</p>}
          </div>
            {/* Show correct answer input only for FillBlank, TypeIn, DragAndDrop */}
            {(formData.type === 'FillBlank' || formData.type === 'TypeIn' || formData.type === 'DragAndDrop') && (
              <div>
                <label htmlFor="correctAnswer" className="block text-sm font-medium text-gray-700 mb-1">
                  Correct Answer {formData.type === 'FillBlank' ? '(what should go in the [blank])' : ''}
                </label>
                <input
                  type="text"
                  id="correctAnswer"
                  name="correctAnswer"
                  className={`w-full px-3 py-2 border ${errors.correctAnswer ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                  placeholder="Enter the correct answer"
                  value={formData.correctAnswer || ''}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                {errors.correctAnswer && <p className="mt-1 text-sm text-red-500">{errors.correctAnswer}</p>}
              </div>
            )}
          {formData.type === 'Matching' && (
            <div className="flex items-center h-full">
              <span className="text-gray-500 italic">Matching pairs are the answer. No separate correct answer box.</span>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const renderOptionsTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-700">Answer Options</h3>
          {canEdit && (
            <button
              type="button"
              onClick={addOption}
              className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Add Option
            </button>
          )}
        </div>
        
        {errors.options && <p className="mt-1 text-sm text-red-500">{errors.options}</p>}
        
        {formData.options && formData.options.length > 0 ? (
          <div className="space-y-3">
            {formData.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="flex-grow">
                  <div className="flex items-center">
                    <span className="w-8 text-center text-gray-500">{index + 1}.</span>
                    <input
                      type="text"
                      className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      value={getOptionText(option)}
                      onChange={(e) => handleOptionsChange(index, e.target.value)}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-md">
            <p className="text-gray-500">No options added yet.</p>
            {canEdit && (
              <button
                type="button"
                onClick={addOption}
                className="mt-2 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Add Option
              </button>
            )}
          </div>
        )}
        
        {formData.type === 'MultipleChoice' && formData.correctAnswer && (
          <div className="mt-4 p-3 bg-green-50 rounded-md">
            <h4 className="text-sm font-medium text-green-800">Correct Answer</h4>
            <p className="text-green-700">{formData.correctAnswer}</p>
          </div>
        )}
      </div>
    );
  };
  
  const renderHintsTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-700">Hints</h3>
          {canEdit && (
            <button
              type="button"
              onClick={addHint}
              className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Add Hint
            </button>
          )}
        </div>
        
        {formData.hints && formData.hints.length > 0 ? (
          <div className="space-y-3">
            {formData.hints.map((hint, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="flex-grow">
                  <div className="flex items-center">
                    <span className="w-8 text-center text-gray-500">#{index + 1}</span>
                    <input
                      type="text"
                      className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      value={hint}
                      onChange={(e) => handleHintsChange(index, e.target.value)}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeHint(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-md">
            <p className="text-gray-500">No hints added yet.</p>
            {canEdit && (
              <button
                type="button"
                onClick={addHint}
                className="mt-2 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Add Hint
              </button>
            )}
          </div>
        )}
      </div>
    );
  };
  
  const renderQuestionPreview = () => {
    if (!formData.type) return null;
    
    const getPreviewContent = () => {
      switch (formData.type) {
        case 'MultipleChoice':
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: formData.text || '' }} /> : <p>{formData.text}</p>}
              </div>
              <div className="space-y-2">
                {formData.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id={`preview-option-${index}`}
                      name="preview-option"
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                      checked={getOptionText(option) === formData.correctAnswer}
                      readOnly
                    />
                    <label htmlFor={`preview-option-${index}`} className="text-gray-700">
                      {getOptionText(option)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );
        
        case 'TypeIn':
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: formData.text || '' }} /> : <p>{formData.text}</p>}
              </div>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Type your answer here..."
                disabled
              />
              <p className="text-sm text-gray-500">Correct answer: {formData.correctAnswer}</p>
            </div>
          );
        
        case 'DragAndDrop':
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: formData.text || '' }} /> : <p>{formData.text}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.options?.map((option, index) => (
                  <div key={index} className="px-3 py-1 bg-gray-100 border border-gray-300 rounded-md cursor-move">
                    {getOptionText(option)}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-2 border border-dashed border-gray-300 rounded-md min-h-[60px] flex items-center justify-center">
                <p className="text-gray-400">Drop items here</p>
              </div>
              <p className="text-sm text-gray-500">Correct answer: {formData.correctAnswer}</p>
            </div>
          );
        
        case 'Matching':
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: formData.text || '' }} /> : <p>{formData.text}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  {formData.options?.slice(0, Math.ceil(formData.options.length / 2)).map((option, index) => (
                    <div key={index} className="p-2 bg-gray-100 border border-gray-300 rounded-md">
                      {getOptionText(option)}
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {formData.options?.slice(Math.ceil(formData.options.length / 2)).map((option, index) => (
                    <div key={index} className="p-2 bg-gray-100 border border-gray-300 rounded-md">
                      {getOptionText(option)}
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-500">Correct answer: {formData.correctAnswer}</p>
            </div>
          );
        
        case 'MultipleSelection':
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: formData.text || '' }} /> : <p>{formData.text}</p>}
              </div>
              <div className="space-y-2">
                {formData.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`preview-option-${index}`}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      checked={
                        Array.isArray(formData.correctAnswer)
                          ? formData.correctAnswer.includes(getOptionText(option))
                          : typeof formData.correctAnswer === 'string'
                            ? formData.correctAnswer.split(',').includes(getOptionText(option))
                            : false
                      }
                      readOnly
                    />
                    <label htmlFor={`preview-option-${index}`} className="text-gray-700">
                      {getOptionText(option)}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500">Correct answer: {formData.correctAnswer}</p>
            </div>
          );
        
        case 'YesNo':
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: formData.text || '' }} /> : <p>{formData.text}</p>}
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="preview-option-yes"
                    name="preview-option-yesno"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    checked={formData.correctAnswer === 'Yes'}
                    readOnly
                  />
                  <label htmlFor="preview-option-yes" className="text-gray-700">
                    Yes
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="preview-option-no"
                    name="preview-option-yesno"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    checked={formData.correctAnswer === 'No'}
                    readOnly
                  />
                  <label htmlFor="preview-option-no" className="text-gray-700">
                    No
                  </label>
                </div>
              </div>
              <p className="text-sm text-gray-500">Correct answer: {formData.correctAnswer}</p>
            </div>
          );
        
        case 'FillBlank':
          // Replace [blank] markers with underscores to show where answers go
          const textWithBlanks = formData.text?.replace(/\[blank\]/g, '_____');
          return (
            <div className="space-y-3">
              <div className="text-gray-800 font-medium">
                {formData.useHtmlFormat ? <div dangerouslySetInnerHTML={{ __html: textWithBlanks || '' }} /> : <p>{textWithBlanks}</p>}
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Fill in the blank..."
                  disabled
                />
              </div>
              <p className="text-sm text-gray-500">Correct answer: {formData.correctAnswer}</p>
            </div>
          );
        
        default:
          return (
            <div className="text-center py-6">
              <p className="text-gray-500">Select a question type to see the preview.</p>
            </div>
          );
      }
    };
    
    return (
      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-700 mb-3">Question Preview</h3>
        <div className="p-4 border border-gray-200 rounded-md bg-white question-preview">
          {getPreviewContent()}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  if (!question) {
    return (
      <div className="p-4">
        <div className="text-center py-8">
          <h2 className="text-xl font-bold text-gray-800">Question Not Found</h2>
          <p className="text-gray-600 mt-2">The question you are looking for does not exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/questions')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Back to Questions
          </button>
        </div>
      </div>
    );
  }

  // (Removed duplicate declarations here. Only keep the original implementations at the top of the component)
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {canEdit ? 'Edit Question' : 'Question Details'}
        </h1>
      </div>
      <Card className="bg-white shadow-sm mb-6">
        <div className="mb-5 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'general' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              General
            </button>
            <button
              onClick={() => setActiveTab('hints')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'hints' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Hints
            </button>
          </nav>
        </div>
        <form id="question-form" onSubmit={handleSubmit}>
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'hints' && renderHintsTab()}
          <div className="flex space-x-2 mt-6">
            <button
              onClick={() => navigate('/admin/questions')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              type="button"
            >
              Back
            </button>
            {canEdit && (
              <button
                type="submit"
                form="question-form"
                className={`px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>
        {renderQuestionPreview()}
      </Card>
    </div>
  );
};

export default QuestionDetailScreen;
